<template>
  <div class="flex flex-col h-full">
    <Teleport to="#topbar-search" defer>
      <SearchInput
        v-model="searchQuery"
        @search="handleSearch"
        placeholder="Search roles..."
        class="w-full"
      />
    </Teleport>
    <DashboardHeader
      :title="`Job Opportunities${displayCount > 0 && !roleStore.isLoadingList ? ` (${displayCount} found)` : ''}`"
      :link="isPublicContext ? { name: 'Public Jobs', query: { ...route.query } } : { name: 'All jobs', query: { ...route.query } }"
    >
      <template #right>
        <div class="w-full flex items-center gap-2 justify-end">
          <router-link
            v-if="!isLimitedUser && !isPublicContext"
            to="/jobs/create-new"
            class="text-xs md:text-xs w-fit font-normal bg-cosmicPurple text-white px-2 pr-4 md:px-4 md:pl-2 py-2 rounded-md flex items-center hover:bg-cosmicPurple/80 transition-all duration-200 whitespace-nowrap"
          >
            <Plus class="inline-block h-4 mr-1" />
            <span class="hidden lg:block">Create new job posting</span>
            <span class="block lg:hidden">New</span>
          </router-link>
        </div>
      </template>
    </DashboardHeader>
    <JobsFilter class="mb-2">
      <nav v-if="!isPublicContext" class="flex gap-2" aria-label="Job source">
        <button
          v-for="tab in sourceTabs"
          :key="tab.label"
          type="button"
          :aria-pressed="tab.active"
          @click="toggleSource(tab.key)"
          :class="[
            'inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-all duration-200',
            tab.active
              ? 'bg-cosmicPurple text-white border-cosmicPurple'
              : 'bg-white text-gray-700 border-gray-200 hover:border-cosmicPurple'
          ]"
        >
          <Sparkles
            v-if="tab.sparkle"
            class="h-3.5 w-3.5 stroke-1 animate-sparkle-pop motion-reduce:animate-none"
            :class="tab.active ? 'fill-white text-white' : 'fill-cosmicPurple text-cosmicPurple'"
            aria-hidden="true"
          />
          {{ tab.label }}
        </button>
      </nav>
    </JobsFilter>

    <!-- Main content section - will scroll -->
    <div
      class="flex w-full flex-1 overflow-hidden border-[1px] border-gray-200 rounded-xl bg-white"
    >
      <!-- RESULTS SECTION -->
      <div class="flex-[3] overflow-y-auto" :class="{ 'hidden xl:block': role_id }">

        <!-- Gate page (public page 6+): blurred dummy cards + register CTA -->
        <div v-if="showGate" class="relative h-full overflow-hidden">
          <ul class="flex flex-col gap-4 w-full mt-4 px-4 select-none pointer-events-none blur-sm overflow-hidden">
            <JobCardSkeleton v-for="n in 5" :key="n" />
          </ul>
          <div class="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm rounded-xl px-6 text-center">
            <Lock class="w-10 h-10 text-cosmicPurple mb-4" />
            <h2 class="text-lg font-semibold text-gray-900 mb-1">There are more jobs waiting</h2>
            <p class="text-sm text-gray-500 mb-6 max-w-xs">
              Register for free to see all open opportunities and track your applications.
            </p>
            <router-link
              to="/register"
              class="bg-cosmicPurple text-white px-8 py-2.5 rounded-full text-sm font-medium hover:bg-cosmicPurple/80 transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              Get Started — It's Free
            </router-link>
            <router-link
              to="/login"
              class="mt-3 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Already have an account? Log in
            </router-link>
          </div>
        </div>

        <!-- Normal content -->
        <template v-else>
          <!-- Loading state -->
          <div v-if="isLoading">
            <ul
              :class="[
                'flex flex-col gap-2 w-full mt-3 px-3',
                viewMode === 'grid' ? 'sm:grid sm:grid-cols-2' : ''
              ]"
            >
              <JobCardSkeleton v-for="n in 6" :key="n" />
            </ul>
          </div>

          <!-- Results -->
          <div v-else>
            <!-- Loading state when no roles -->
            <ul
              v-if="!roleStore.roles.length && roleStore.isLoadingList"
              :class="[
                'flex flex-col gap-2 w-full mt-3 px-3',
                viewMode === 'grid' ? 'sm:grid sm:grid-cols-2' : ''
              ]"
            >
              <JobCardSkeleton v-for="n in 6" :key="n" />
            </ul>

            <!-- No results message -->
            <div
              v-else-if="!roleStore.roles.length && !roleStore.isLoadingList"
              class="flex flex-col items-center justify-center h-full min-h-[500px]"
            >
              <HeartCrack class="w-6 h-6 text-gray-400 mb-4" />
              <h3 class="text-sm text-gray-900 mb-1">No job postings found</h3>
            </div>

            <!-- Results list -->
            <ul
              v-else
              :class="[
                'fadeIn flex flex-col gap-2 w-full mt-3 px-3 transition-all duration-200',
                viewMode === 'grid' ? 'sm:grid sm:grid-cols-2' : '',
                roleStore.isLoadingList ? 'opacity-50' : 'opacity-100'
              ]"
            >
              <JobCard
                v-for="role in roleStore.roles"
                :key="role.id"
                :role="role"
              />
            </ul>
          </div>

          <!-- Pagination -->
          <PaginationComponent
            v-if="roleStore.roles.length || isPublicContext"
            :current-page="currentPage"
            :total-pages="paginationTotalPages"
            :use-router="true"
            :additional-query-params="{ ...route.query }"
          />
        </template>
      </div>

      <!-- APPLICATION SECTION -->
      <router-view class="w-full flex-[5]" @filter-loading="handleFilterLoading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SearchInput from '@/components/common/SearchInput.vue'
import JobCard from '@/components/jobs/JobCard.vue'
import JobsFilter from '@/components/jobs/JobsFilter.vue'
import { useRolesStore } from '@/stores/roles'
import { useViewMode } from '@/composables/useViewMode'
import { HeartCrack, Plus, Lock, Sparkles } from 'lucide-vue-next'
import JobCardSkeleton from '@/components/jobs/JobCardSkeleton.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import { useAuth } from '@/composables/useAuth'
import { urlToFilters } from '@/utils/urlToFilters'

const route = useRoute()
const router = useRouter()
const viewMode = useViewMode('rolesViewMode') as Ref<'single' | 'grid'>
const { isLimitedUser } = useAuth()

const role_id = computed(() => route.params.role_id)
const roleStore = useRolesStore()

const isPublicContext = computed(() => route.path.startsWith('/explore/'))

// Community and Discover toggle independently (both on = no source param); at least one stays on.
const activeSources = computed(() => ({
  community: route.query.source !== 'discover',
  discover: route.query.source !== 'community'
}))
const sourceTabs = computed(() => [
  { key: 'community' as const, label: 'Community', active: activeSources.value.community, sparkle: false },
  { key: 'discover' as const, label: 'Discover', active: activeSources.value.discover, sparkle: true }
])

const SOURCE_STORAGE_KEY = 'jobsSource'

function toggleSource(key: 'community' | 'discover') {
  const next = { ...activeSources.value, [key]: !activeSources.value[key] }
  if (!next.community && !next.discover) return
  const { source, ...rest } = route.query
  void source
  const nextSource = next.community && next.discover ? undefined : next.discover ? 'discover' : 'community'
  localStorage.setItem(SOURCE_STORAGE_KEY, nextSource ?? '')
  void router.push({ query: { ...rest, page: '1', ...(nextSource && { source: nextSource }) } })
}

async function applySavedSource(query: typeof route.query): Promise<boolean> {
  const saved = localStorage.getItem(SOURCE_STORAGE_KEY)
  if (isPublicContext.value || query.source || (saved !== 'community' && saved !== 'discover')) return false
  await router.replace({ query: { ...query, source: saved } })
  return true
}

const displayCount = computed(() => roleStore.realTotalCount ?? roleStore.totalCount)
const totalPages = computed(() => Math.ceil(roleStore.totalCount / roleStore.pageSize))

// For public users, always expose page 6 as a gate even if real content is fewer pages
const paginationTotalPages = computed(() =>
  isPublicContext.value ? Math.max(totalPages.value, 6) : totalPages.value
)

const currentPage = ref(1)
const searchQuery = ref('')
const searchTimeout = ref<number | null>(null)
const isLoading = ref(false)

// Show blurred gate on page 6+ in public context
const showGate = computed(() => isPublicContext.value && currentPage.value >= 6)

function handleSearch() {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }

  searchTimeout.value = setTimeout(async () => {
    const { page, ...existingFilters } = route.query
    void page

    const newQuery: Record<string, string> = {
      ...(existingFilters as Record<string, string>),
      page: '1'
    }

    if (searchQuery.value.trim()) {
      newQuery.search = searchQuery.value.trim()
    } else {
      delete newQuery.search
    }

    await router.push({ query: newQuery })
  }, 600) as unknown as number
}

watch(
  () => route.query.search,
  (newSearch) => {
    searchQuery.value = (newSearch as string) || ''
  },
  { immediate: true }
)

const handleFilterLoading = (loading: boolean) => {
  isLoading.value = loading
}

watch(
  () => route.query,
  async (query, oldQuery) => {
    if (!query || JSON.stringify(query) === JSON.stringify(oldQuery)) return

    // If no status is set and this is initial load, default to open jobs
    if (!query.status && !oldQuery) {
      await router.replace({
        query: { ...query, status: 'true' }
      })
      return
    }

    if (await applySavedSource(query)) return

    const page = query.page ? parseInt(query.page as string) : 1
    currentPage.value = page
    searchQuery.value = (query.search as string) || ''

    // Skip the API call when showing the gate page
    if (isPublicContext.value && page >= 6) return

    isLoading.value = true

    try {
      const search = query.search as string | undefined
      const filters = urlToFilters(query)

      await roleStore.fetchRolesWithParams({ page, search, filters })

      if (page !== (oldQuery?.page ? parseInt(oldQuery.page as string) : 1)) {
        window.scrollTo(0, 0)
      }
    } finally {
      isLoading.value = false
    }
  },
  { immediate: true }
)
</script>